import React, { useState, useEffect } from "react";
import AgentEffectivenessSearch from "./agenteffectiveness/AgentEffectivenessSearch";
import DoughnutChart from "./agenteffectiveness/DoughnutChart";
import FadeIn from "./compliance/FadeIn";
import { Grid as GridLoading } from "react-loader-spinner";
import HeatmapChart from "./agenteffectiveness/HeatmapChart";
import Highlighter from "react-highlight-words";
import html2canvas from "html2canvas";
import Markdown from "react-markdown";
import { styled } from "@mui/material/styles";
import { theme } from "../theme";
import VerticalBarChart from "./agenteffectiveness/VerticalBarChart";
import "../assets/styles/AgentEffectiveness.scss";

// Import MUI elements
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Grid, Paper } from "@mui/material";

// Import framework to manage datepicker
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Import icons
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SendIcon from "@mui/icons-material/Send";
import SquareIcon from "@mui/icons-material/Square";

const Item = styled(Paper)(({ theme }) => ({
  padding: 10,
}));

const BoxContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const TranscriptModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1250,
  height: 750,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 850,
  height: 600,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const ImageStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: 800,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
  padding: 0,
};

function formatDate(dateString: string) {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const date = new Date(dateString);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  const formattedDate = `${day} ${month} ${year}`;

  return formattedDate;
}

function AgentEffectiveness({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild;

  const [layout, setLayout] = React.useState<string | null>("agent");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("FLG3NWF");
  const [agentData, setAgentData] = useState<any>(null);
  const [agentList, setAgentList] = useState<any[]>([]);
  const [callSummaryData, setCallSummaryData] = useState<any>(null);
  const [talkTrackData, setTalkTrackData] = useState<any>(null);
  const [agentPerformanceData, setAgentPerformanceData] = useState<any>(null);
  const [goodTranscriptsData, setGoodTranscriptsData] = useState<any>([]);
  const [badTranscriptsData, setBadTranscriptsData] = useState<any>([]);

  const [open, setOpen] = useState<boolean>(false);
  const [selectedTranscript, setSelectedTranscript] = useState<
    [string, any[], string]
  >(["", [], ""]);
  const [highlightsData, setHighlightsData] = useState<any>([]);

  const [isFeedbackLoading, setIsFeedbackLoading] = useState<boolean>(true);
  const [agentFeedback, setAgentFeedback] = useState<any>(null);
  const [expandClicked, setExpandClicked] = useState<boolean>(false);

  // Add state for selected option and start date
  const [selectedOption, setSelectedOption] = useState<string>("Last 7 days");
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs("2024-01-31"));
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs("2024-01-25"));

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Daily");
  const [recurrences, setRecurrences] = useState<string>("1");
  const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
  const Today = dayjs();

  const handleLayout = (
    event: React.MouseEvent<HTMLElement>,
    newLayout: string | null
  ) => {
    if (newLayout !== null) {
      setLayout(newLayout);
    }
  };

  useEffect(() => {
    if (
      rangeStartDate &&
      rangeEndDate &&
      rangeStartDate.isAfter(rangeEndDate, "day")
    ) {
      setRangeStartDate(rangeEndDate);
    }
  }, [rangeStartDate, rangeEndDate]);

  // Set screenshot data for send report button
  const [screenshot, setScreenshot] = useState<any>();
  const [isImageOpen, setIsImageOpen] = useState<boolean>(false);

  const onButtonClick = async () => {
    const canvas = await html2canvas(document.body);
    const dataUrl = canvas.toDataURL("image/png");

    setScreenshot(dataUrl);
  };

  // Handle click for call center dropbown filter
  const handleAgentIdChange = (
    newSelection: string,
    selectedAgentId: string
  ) => {
    if (newSelection !== selectedAgentId) {
      setSelectedAgentId(newSelection);
    }
  };

  // Updated DatePicker onChange handler
  const handleEndDateChange = (
    selectedOption: string,
    newDate: dayjs.Dayjs
  ) => {
    setEndDate(newDate);
    let calculatedStartDate;

    const calculateUpdatedStartDate = () => {
      calculatedStartDate = newDate;

      if (selectedOption === "Last 7 days" && newDate instanceof dayjs) {
        calculatedStartDate = newDate.subtract(6, "days");
      } else if (
        selectedOption === "Last 30 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(30, "days");
      } else if (
        selectedOption === "Last 60 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(60, "days");
      }
      setStartDate(calculatedStartDate);
      setRangeStartDate(calculatedStartDate);
    };
    calculateUpdatedStartDate();
  };

  useEffect(() => {
    const url = "/agent_list";

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setAgentList(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, []);

  useEffect(() => {
    const url = `/agent_info?agent_id=${selectedAgentId}`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setAgentData(result[0]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [selectedAgentId]);

  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(`/get_agent_call_summary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            agent_id: selectedAgentId,
          }),
        });
        const result = await response.json();
        setCallSummaryData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedAgentId]);

  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(`/get_agent_talk_track`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            agent_id: selectedAgentId,
          }),
        });
        const result = await response.json();
        setTalkTrackData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedAgentId]);

  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(`/get_agent_performance`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            agent_id: selectedAgentId,
          }),
        });
        const result = await response.json();
        setAgentPerformanceData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedAgentId]);

  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(
          `/agent_yes_transcripts_get?agent_id=${selectedAgentId}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
        );
        if (response.status === 200) {
          const result = await response.json();
          setGoodTranscriptsData(result);
        } else {
          setGoodTranscriptsData([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedAgentId]);

  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(
          `/agent_no_transcripts_get?agent_id=${selectedAgentId}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
        );
        if (response.status === 200) {
          const result = await response.json();
          setBadTranscriptsData(result);
        } else {
          setBadTranscriptsData([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedAgentId]);

  useEffect(() => {
    setIsFeedbackLoading(true);
    let controller = new AbortController();
    let signal = controller.signal;

    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await fetch(`/generate_agent_feedback`, {
          signal,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            agent_id: selectedAgentId,
          }),
        });

        if (response.status === 200) {
          const result = await response.json();
          setAgentFeedback(result.feedback);
          setIsFeedbackLoading(false);
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Request aborted");
        } else {
          console.error("Error fetching data:", error);
        }
      }
    };
    fetchData();

    return () => {
      controller.abort();
      controller = new AbortController();
    };
  }, [startDate, endDate, selectedAgentId]);

  const handleOpen = (row: any) => {
    // console.log(row);

    let cleanTranscript = row["transcript"].replace(/\*/g, "");

    let agentIndex = cleanTranscript.indexOf("Agent:");

    let finalTranscript;
    if (agentIndex !== -1) {
      finalTranscript = cleanTranscript.substring(agentIndex);
    } else {
      finalTranscript = cleanTranscript;
    }

    const transcriptArray = finalTranscript
      .split(/(?=Agent(\s[A-Za-z]+)?:|Customer:)/)
      .filter(
        (segment: string) =>
          segment !== undefined && segment.trim().split(/\s+/).length > 1
      ) // Filter out undefined segments
      .map((segment: string, index: number) => {
        if (index % 2 === 0) {
          return segment.trim();
        } else {
          return `\n${segment}`;
        }
      });

    setSelectedTranscript([
      row["transcript_id"],
      transcriptArray,
      row["transcript"],
    ]);

    // Set highlights data
    const agentAlternativePaymentsArray = [];
    const agentAlternativePaymentPromotionArray = [];
    const agentPromotionArray = [];
    const agentCompetitorUsageArray = [];
    const agentVendorAcceptanceArray = [];
    const agentSpendProfileArray = [];
    const agentDataHistoryArray = [];
    const agentRateMatchArray = [];

    agentAlternativePaymentsArray.push(
      row["Agent_Asks_About_Alternative_Payments_Sentence"] !== "NULL"
        ? row["Agent_Asks_About_Alternative_Payments_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentAlternativePaymentPromotionArray.push(
      row["Agent_Suggests_Alternative_Payment_Promotion_Sentence"] !== "NULL"
        ? row["Agent_Suggests_Alternative_Payment_Promotion_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentPromotionArray.push(
      row["Agent_Suggests_Promotion_Sentence"] !== "NULL"
        ? row["Agent_Suggests_Promotion_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentCompetitorUsageArray.push(
      row["Agent_Asks_About_Competitor_Usage_Sentence"] !== "NULL"
        ? row["Agent_Asks_About_Competitor_Usage_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentVendorAcceptanceArray.push(
      row["Agent_Asks_About_Vendor_Acceptance_Sentence"] !== "NULL"
        ? row["Agent_Asks_About_Vendor_Acceptance_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentSpendProfileArray.push(
      row["Agent_Discusses_Spend_Profile_Sentence"] !== "NULL"
        ? row["Agent_Discusses_Spend_Profile_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentDataHistoryArray.push(
      row["Agent_Points_Out_Data_History_Sentence"] !== "NULL"
        ? row["Agent_Points_Out_Data_History_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    agentRateMatchArray.push(
      row["Agent_Suggests_Rate_Match_Sentence"] !== "NULL"
        ? row["Agent_Suggests_Rate_Match_Sentence"]
            .split("\\n")[0]
            .replace(/^Agent: /, "")
        : ""
    );

    setHighlightsData([
      agentAlternativePaymentsArray,
      agentAlternativePaymentPromotionArray,
      agentPromotionArray,
      agentCompetitorUsageArray,
      agentVendorAcceptanceArray,
      agentSpendProfileArray,
      agentDataHistoryArray,
      agentRateMatchArray,
    ]);

    setOpen(true);
  };

  // console.log(highlightsData);

  const Highlight = ({
    children,
    highlightIndex,
  }: {
    children: React.ReactNode;
    highlightIndex: number;
  }) => {
    const colors = [
      "#ffe404", // Alternative_Payments_Sentence
      "#ffe404", // Alternative_Payments_Sentence_Promotion
      "#b7ad6c", // Promotion
      "#907c1c", // Competitor_Usage_Sentence
      "#c4c4cd", // Vendor_Acceptance_Sentence
      mode === "light" ? "#2e2e38" : "white", // Spend_Profile_Sentence
      mode === "light" ? "#2e2e38" : "white", // Data_History_Sentence
      mode === "light" ? "#2e2e38" : "white", // Agent_Rate_Match_Sentence
    ];
    const fontColors = [
      "black", // Alternative_Payments_Sentence
      "black", // Alternative_Payments_Sentence_Promotion
      "black", // Promotion
      "white", // Competitor_Usage_Sentence
      "white", // Vendor_Acceptance_Sentence
      mode === "light" ? "white" : "black", // Spend_Profile_Sentence
      mode === "light" ? "white" : "black", // Data_History_Sentence
      mode === "light" ? "white" : "black", // Agent_Rate_Match_Sentence
    ];
    const color = colors[highlightIndex % colors.length];
    const fontColor = fontColors[highlightIndex % fontColors.length];
    return (
      <span
        style={{
          backgroundColor: color,
          fontFamily: theme.fonts.primary,
          color: fontColor,
        }}
      >
        {children}
      </span>
    );
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSendReport = () => {
    //console.log('Sending report:', { email, comments });
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openImage = () => {
    setIsImageOpen(true);
  };

  const closeImage = () => {
    setIsImageOpen(false);
  };

  const handleReset = () => {
    setSelectedOption("Last 7 days");
    setEndDate(dayjs("2024-01-31"));
    setRangeStartDate(dayjs("2024-01-24"));
    setSelectedAgentId("FLG3NWF");
    setLayout("agent");
  };

  return (
    <FadeIn transitionDuration={700}>
      <div className='agenteffect-root container'>
        <FadeIn transitionDuration={700}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Item className='exec-navbar-filters'>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={2} lg={1.5} xl={1.5}>
                        <FormControl fullWidth>
                          <InputLabel id='select-agent-id'>Agent ID</InputLabel>
                          <Select
                            labelId='select-agent-id-label'
                            id='select-agent-id'
                            value={selectedAgentId}
                            onChange={(e) => {
                              setSelectedAgentId(e.target.value);
                              handleAgentIdChange(
                                e.target.value,
                                selectedAgentId
                              );
                            }}
                          >
                            {agentList &&
                              agentList.length > 0 &&
                              agentList.map((item) => (
                                <MenuItem
                                  key={item["agent_id"]}
                                  value={item["agent_id"]}
                                >
                                  {item["agent_name"]}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <FormControl fullWidth>
                          <InputLabel id='select-label'>
                            Activity Analysis Period
                          </InputLabel>
                          <Select
                            labelId='select-label'
                            id='select'
                            value={selectedOption}
                            onChange={(e) => {
                              setSelectedOption(e.target.value);
                              handleEndDateChange(e.target.value, endDate);
                            }}
                          >
                            <MenuItem value='Last 7 days'>Last 7 days</MenuItem>
                            <MenuItem value='Last 30 days'>
                              Last 30 days
                            </MenuItem>
                            <MenuItem value='Last 60 days'>
                              Last 60 days
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale='en'
                        >
                          <div>
                            <DatePicker
                              defaultValue={dayjs("2024-01-31")}
                              label='Report Date'
                              value={endDate}
                              onChange={(e) => {
                                if (e) {
                                  handleEndDateChange(selectedOption, e);
                                }
                              }}
                            />
                          </div>
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={12} md={5} lg={4} xl={4}>
                        <div className='exec-buttons'>
                          <Button
                            className='agenteffect-nav-btn exec-nav-btn'
                            variant='contained'
                            endIcon={<RestartAltIcon />}
                            onClick={handleReset}
                          >
                            Reset Filters
                          </Button>
                          <Button
                            className='agenteffect-nav-btn exec-nav-btn'
                            variant='contained'
                            endIcon={<SendIcon />}
                            onClick={() => {
                              onButtonClick();
                              openModal();
                            }}
                          >
                            Send Report
                          </Button>
                        </div>
                      </Grid>

                      <Grid
                        className='switch-toggle'
                        item
                        xs={12}
                        sm={12}
                        md={3}
                        lg={3.5}
                        xl={3.5}
                      >
                        <ToggleButtonGroup
                          value={layout}
                          exclusive
                          onChange={handleLayout}
                          aria-label='layout toggle button group'
                          className='layout-toggle'
                        >
                          <ToggleButton value='agent' aria-label='agent layout'>
                            <PersonIcon />
                            <span>Agent</span>
                          </ToggleButton>
                          <ToggleButton
                            value='overview'
                            aria-label='overview layout'
                          >
                            <PeopleIcon />
                            <span>Overview</span>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>

                      <Modal
                        className='exec-send-modal modal-global'
                        open={isModalOpen}
                        onClose={closeModal}
                        aria-labelledby='modal-modal-title'
                        aria-describedby='modal-modal-description'
                      >
                        <Box sx={ModalStyle}>
                          <h3>
                            <SendIcon /> Report Form
                          </h3>
                          <div>
                            <TextField
                              label='Subject'
                              variant='outlined'
                              fullWidth
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              margin='normal'
                            />
                            <TextField
                              label='Email'
                              variant='outlined'
                              fullWidth
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              margin='normal'
                            />
                            <TextField
                              label='Additional Comments'
                              variant='outlined'
                              fullWidth
                              multiline
                              rows={4}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              margin='normal'
                            />
                            <FormControl
                              fullWidth
                              className='send-report-dropdown'
                            >
                              <InputLabel id='select-label'>
                                Report Frequency
                              </InputLabel>
                              <Select
                                labelId='select-label'
                                id='select'
                                value={frequency}
                                onChange={(e) => {
                                  setFrequency(e.target.value);
                                }}
                              >
                                <MenuItem value='Daily'>Daily</MenuItem>
                                <MenuItem value='Weekly'>Weekly</MenuItem>
                                <MenuItem value='Monthly'>Monthly</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              label='Num of Recurrences'
                              variant='outlined'
                              fullWidth
                              type='number'
                              value={recurrences}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (
                                  !isNaN(value) &&
                                  value >= 0 &&
                                  value <= 365
                                ) {
                                  setRecurrences(value.toString());
                                } else if (e.target.value === "") {
                                  setRecurrences("");
                                }
                              }}
                              margin='normal'
                              inputProps={{ inputMode: "numeric", max: 365 }}
                            />
                            <p className='text-dark-mode'>Recurrence Range:</p>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale='en'
                            >
                              <div className='send-report-datepickers'>
                                <DatePicker
                                  className='exec-date-picker'
                                  label='Start'
                                  value={rangeStartDate}
                                  onChange={(date) => {
                                    if (date && date.isBefore(Today, "day")) {
                                      setRangeStartDate(Today);
                                    } else {
                                      setRangeStartDate(date || Today);
                                    }
                                  }}
                                />
                                <span className='text-dark-mode'> to </span>
                                <DatePicker
                                  className='exec-date-picker'
                                  label='End'
                                  value={rangeEndDate}
                                  onChange={(date) => {
                                    if (date && date.isBefore(Today, "day")) {
                                      setRangeEndDate(Today);
                                    } else if (
                                      rangeStartDate &&
                                      date &&
                                      date.isBefore(rangeStartDate, "day")
                                    ) {
                                      setRangeEndDate(rangeStartDate);
                                    } else {
                                      setRangeEndDate(date || Today);
                                    }
                                  }}
                                />
                              </div>
                            </LocalizationProvider>
                            {screenshot && (
                              <div className='send-report-attachment'>
                                <p>Attachment:</p>
                                <img
                                  src={screenshot}
                                  alt='Screenshot'
                                  onClick={openImage}
                                />
                              </div>
                            )}
                          </div>
                          <div>
                            <Button
                              className='exec-send-btn'
                              variant='contained'
                              onClick={handleSendReport}
                            >
                              Send
                            </Button>
                          </div>
                        </Box>
                      </Modal>
                      <Modal
                        className='exec-send-modal modal-global'
                        open={isImageOpen}
                        onClose={closeImage}
                        aria-labelledby='modal-modal-title'
                        aria-describedby='modal-modal-description'
                      >
                        <Box sx={ImageStyle}>
                          <div className='magnified-content'>
                            <img src={screenshot} alt='Magnified Screenshot' />
                          </div>
                        </Box>
                      </Modal>
                    </Grid>
                  </Box>
                </Item>
              </Grid>
            </Grid>

            {layout === "agent" && (
              <Grid
                className='agenteffect-container-wrapper'
                container
                spacing={2}
              >
                <Grid
                  className='agenteffect-paper-wrapper'
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <BoxContainer className='agenteffect-left-container'>
                    <h3>Agent Statistics</h3>
                    <Grid container spacing={2} className='agent-profile'>
                      <Grid
                        className='profile-img'
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={3}
                        xl={3}
                      >
                        <Avatar className='profile_avatar' />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={9} xl={9}>
                        <div className='agent-profile-info'>
                          <h2>{agentData && agentData.agent_name}</h2>
                          <p>
                            {agentData && agentData.agent_id} |{" "}
                            {agentData && agentData.agent_email}
                          </p>
                          <p>{agentData && agentData.agent_address}</p>
                        </div>
                      </Grid>
                    </Grid>
                    <Divider className='agenteffect-divider' />
                    <h3>Total Calls Handled</h3>
                    <div>
                      <React.StrictMode>
                        <DoughnutChart
                          parentToChild={{ mode, callSummaryData }}
                        />
                      </React.StrictMode>
                    </div>
                    <Divider className='agenteffect-divider' />
                    <h3>
                      Talk Track Component Comparison
                      {mode === "dark" ? (
                        <span className='shine'>
                          <AutoAwesomeIcon />
                        </span>
                      ) : (
                        <span className='ai-driven'>
                          <AutoAwesomeIcon />
                        </span>
                      )}
                    </h3>
                    <VerticalBarChart parentToChild={{ mode, talkTrackData }} />
                  </BoxContainer>
                </Grid>

                <Grid
                  className='agenteffect-paper-wrapper'
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={3.5}
                  xl={3.5}
                >
                  <BoxContainer className='agenteffect-paper-container container-middle'>
                    <h3>
                      Agent Performance Metrics
                      {mode === "dark" ? (
                        <span className='shine'>
                          <AutoAwesomeIcon />
                        </span>
                      ) : (
                        <span className='ai-driven'>
                          <AutoAwesomeIcon />
                        </span>
                      )}
                    </h3>
                    <TableContainer
                      className='agenteffect-table-container middle-table-container'
                      component={Paper}
                    >
                      <Table
                        className='agenteffect-table middle-table'
                        size='small'
                        aria-label='a dense table'
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Metric
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              Agent
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              Average
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              Top
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Total Calls
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Total_Calls.Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Total_Calls
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Total_Calls.Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Calls to Voicemail
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Calls_to_Voicemail
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Calls_to_Voicemail
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Calls_to_Voicemail
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Non-Customer Interactions
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData
                                      .Non_Customer_Interactions.Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData
                                      .Non_Customer_Interactions
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData
                                      .Non_Customer_Interactions.Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Customer Busy
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Customer_Busy
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Customer_Busy
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Customer_Busy
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Promotions Discussed
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Discussed
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Discussed
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Discussed
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Promotions Accepted
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Accepted
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Accepted
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Accepted
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Competitor Mentions
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Competitor_Mentions
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Competitor_Mentions
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Competitor_Mentions
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <h3>Effective Transcripts</h3>
                    {goodTranscriptsData && goodTranscriptsData.length > 0 ? (
                      <TableContainer className='agenteffect-sample-transcripts'>
                        <Table
                          className='compliance-clickable-table'
                          size='small'
                          aria-label='a dense table'
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ width: "30%" }} align='left'>
                                Date
                              </TableCell>
                              <TableCell
                                style={{ width: "30%" }}
                                align='center'
                              >
                                Transcript ID
                              </TableCell>
                              <TableCell
                                style={{ width: "30%" }}
                                align='center'
                              >
                                Sentiment
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {goodTranscriptsData.map(
                              (item: any, index: number) => (
                                <TableRow
                                  key={index}
                                  component='tr'
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                  onClick={() => {
                                    handleOpen(item);
                                  }}
                                >
                                  <TableCell
                                    style={{ width: "30%" }}
                                    align='left'
                                  >
                                    {formatDate(item.date)}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "30%" }}
                                    align='center'
                                  >
                                    {item.transcript_id}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "30%" }}
                                    align='center'
                                  >
                                    {item.Sentiment}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <div className='agent-effect-placeholder'>
                        <p>
                          <SearchOffIcon />
                          No transcripts found
                        </p>
                      </div>
                    )}

                    <h3>Missed Opportunities</h3>
                    {badTranscriptsData && badTranscriptsData.length > 0 ? (
                      <TableContainer className='agenteffect-sample-transcripts'>
                        <Table
                          className='compliance-clickable-table'
                          size='small'
                          aria-label='a dense table'
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ width: "30%" }} align='left'>
                                Date
                              </TableCell>
                              <TableCell
                                style={{ width: "30%" }}
                                align='center'
                              >
                                Transcript ID
                              </TableCell>
                              <TableCell
                                style={{ width: "30%" }}
                                align='center'
                              >
                                Sentiment
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {badTranscriptsData.map(
                              (item: any, index: number) => (
                                <TableRow
                                  key={index}
                                  component='tr'
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                  onClick={() => {
                                    handleOpen(item);
                                  }}
                                >
                                  <TableCell
                                    style={{ width: "30%" }}
                                    align='left'
                                  >
                                    {formatDate(item.date)}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "30%" }}
                                    align='center'
                                  >
                                    {item.transcript_id}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "30%" }}
                                    align='center'
                                  >
                                    {item.Sentiment}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <div className='agent-effect-placeholder'>
                        <p>
                          <SearchOffIcon />
                          No transcripts found
                        </p>
                      </div>
                    )}
                  </BoxContainer>
                </Grid>

                <Grid
                  className='agenteffect-paper-wrapper'
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={4.5}
                  xl={4.5}
                >
                  <BoxContainer className='agenteffect-paper-container agenteffect-container-right-section'>
                    <h3>
                      AI Insights and Recommendations
                      {mode === "dark" ? (
                        <span className='shine'>
                          <AutoAwesomeIcon />
                        </span>
                      ) : (
                        <span className='ai-driven'>
                          <AutoAwesomeIcon />
                        </span>
                      )}
                    </h3>
                    <div className='markdown-section agent-effectiveness-feedback'>
                      {!isFeedbackLoading && agentFeedback ? (
                        <>
                          <Markdown
                            className={`expand-agent-feedback ${
                              expandClicked ? "expand-clicked" : ""
                            }`}
                          >
                            {agentFeedback}
                          </Markdown>
                          <span
                            className='expand-readmore-btn'
                            onClick={() => {
                              setExpandClicked((prev) => !prev);
                            }}
                          >
                            {expandClicked ? (
                              <>
                                Show Less <KeyboardDoubleArrowUpIcon />
                              </>
                            ) : (
                              <>
                                Show More <KeyboardDoubleArrowDownIcon />
                              </>
                            )}
                          </span>
                        </>
                      ) : isFeedbackLoading ? (
                        <div className='position-reletive'>
                          <Skeleton width='100%' height='600px' />
                          <span className='centered-text adjust-left'>
                            <GridLoading
                              visible={true}
                              height='15'
                              width='15'
                              color={mode === "dark" ? "white" : "black"}
                              ariaLabel='grid-loading'
                              radius='12.5'
                              wrapperStyle={{}}
                              wrapperClass='grid-wrapper'
                            />
                            AI is generating feedback for the agent...
                          </span>
                        </div>
                      ) : (
                        <p>No data available</p>
                      )}
                    </div>
                  </BoxContainer>
                </Grid>
              </Grid>
            )}

            {layout === "overview" && (
              <Grid
                className='agenteffect-container-wrapper'
                container
                spacing={2}
              >
                <Grid
                  className='agenteffect-paper-wrapper'
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <BoxContainer className='agenteffect-left-container'>
                    <h3>Agent Statistics</h3>
                    <Grid container spacing={2} className='agent-profile'>
                      <Grid
                        className='profile-img'
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={3}
                        xl={3}
                      >
                        <Avatar className='profile_avatar' />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={9} xl={9}>
                        <div className='agent-profile-info'>
                          <h2>{agentData && agentData.agent_name}</h2>
                          <p>
                            {agentData && agentData.agent_id} |{" "}
                            {agentData && agentData.agent_email}
                          </p>
                          <p>{agentData && agentData.agent_address}</p>
                        </div>
                      </Grid>
                    </Grid>
                    <Divider className='agenteffect-divider' />
                    <h3>Total Calls Handled</h3>
                    <div>
                      <React.StrictMode>
                        <DoughnutChart
                          parentToChild={{ mode, callSummaryData }}
                        />
                      </React.StrictMode>
                    </div>

                    <Divider className='agenteffect-divider' />

                    <h3>
                      Agent Performance Metrics
                      {mode === "dark" ? (
                        <span className='shine'>
                          <AutoAwesomeIcon />
                        </span>
                      ) : (
                        <span className='ai-driven'>
                          <AutoAwesomeIcon />
                        </span>
                      )}
                    </h3>
                    <TableContainer
                      className='agenteffect-table-container middle-table-container'
                      component={Paper}
                    >
                      <Table
                        className='agenteffect-table middle-table'
                        size='small'
                        aria-label='a dense table'
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Metric
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              Agent
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              Average
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              Top
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Total Calls
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Total_Calls.Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Total_Calls
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Total_Calls.Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Calls to Voicemail
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Calls_to_Voicemail
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Calls_to_Voicemail
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Calls_to_Voicemail
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Non-Customer Interactions
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData
                                      .Non_Customer_Interactions.Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData
                                      .Non_Customer_Interactions
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData
                                      .Non_Customer_Interactions.Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Customer Busy
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Customer_Busy
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Customer_Busy
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Customer_Busy
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Promotions Discussed
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Discussed
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Discussed
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Discussed
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Promotions Accepted
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Accepted
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Accepted
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Promotions_Accepted
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                            <TableCell style={{ width: "70%" }} align='left'>
                              Competitor Mentions
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Competitor_Mentions
                                      .Agent_value
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Competitor_Mentions
                                      .Top_agents_percent
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                            <TableCell style={{ width: "10%" }} align='center'>
                              {agentPerformanceData
                                ? parseFloat(
                                    agentPerformanceData.Competitor_Mentions
                                      .Top_decile
                                  ).toFixed(1) + "%"
                                : 0}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </BoxContainer>
                </Grid>
                <Grid
                  className='agenteffect-paper-wrapper'
                  item
                  xs={12}
                  sm={12}
                  md={9}
                  lg={8}
                  xl={8}
                >
                  <BoxContainer className='agenteffect-left-container'>
                    <h3>Agents Performance Overview</h3>
                    <HeatmapChart props={{ mode }} />
                  </BoxContainer>
                </Grid>
              </Grid>
            )}

            <Grid
              className='agenteffect-container-wrapper'
              container
              spacing={2}
            >
              <Grid item xs={12}>
                <Item className='ops-card ops-search-section'>
                  <div className='ops-search-header'>
                    <h3>Transcript Semantic Search</h3>
                  </div>
                  <React.StrictMode>
                    <AgentEffectivenessSearch
                      parentToChild={{
                        startDate,
                        endDate,
                        selectedOption,
                        mode,
                      }}
                    />
                  </React.StrictMode>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </FadeIn>
      </div>

      {selectedTranscript[1].length > 0 && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
          className='modal-global'
        >
          <Box className='modal-style' sx={TranscriptModalStyle}>
            <div className='modal-header'>
              <h3>
                <SummarizeIcon /> Full Transcript
              </h3>
            </div>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={7.5} xl={7.5}>
                  <Card variant='outlined' className='get-insights-card'>
                    <CardContent>
                      <Typography
                        id='modal-modal-description transcript-show-modal'
                        sx={{ mt: 2 }}
                      >
                        {selectedTranscript[1].map(
                          (segment: string, index: number) => (
                            <React.Fragment key={index}>
                              <Highlighter
                                highlightClassName='highlighted-sentence'
                                searchWords={highlightsData.flat()}
                                autoEscape={true}
                                caseSensitive={true}
                                textToHighlight={segment}
                                highlightTag={(props: {
                                  children: React.ReactNode;
                                }) => (
                                  <Highlight
                                    {...props}
                                    highlightIndex={highlightsData.findIndex(
                                      (highlight: string) =>
                                        highlight.includes(
                                          props.children as string
                                        )
                                    )}
                                  />
                                )}
                              />
                              <br />
                              <br />
                            </React.Fragment>
                          )
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4.5} xl={4.5}>
                  <Card
                    variant='outlined'
                    className='transcript-card transcript-highlighting highlight-legend'
                  >
                    {highlightsData && highlightsData.length > 0 ? (
                      <CardContent>
                        <div className='legend-flex'>
                          <div>
                            <p>
                              <SquareIcon className='color-alt-payment' />{" "}
                              Alternative Payments Asked
                            </p>
                            <span>
                              {highlightsData[0][0] === "NULL" ||
                              highlightsData[0][0] === undefined ||
                              highlightsData[0][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-payment-promo-suggested' />{" "}
                              Payment Promotion Suggested
                            </p>
                            <span>
                              {highlightsData[1][0] === "NULL" ||
                              highlightsData[1][0] === undefined ||
                              highlightsData[1][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-promo-suggested' />{" "}
                              Transaction Promotion Suggested
                            </p>
                            <span>
                              {highlightsData[2][0] === "NULL" ||
                              highlightsData[2][0] === undefined ||
                              highlightsData[2][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <Divider />

                        <div className='legend-flex'>
                          <div>
                            <p>
                              <SquareIcon className='color-competitor' />{" "}
                              Competitor Usage Asked
                            </p>
                            <span>
                              {highlightsData[3][0] === "NULL" ||
                              highlightsData[3][0] === undefined ||
                              highlightsData[3][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-vendor-acceptance' />{" "}
                              Vendor Acceptance Asked
                            </p>
                            <span>
                              {highlightsData[4][0] === "NULL" ||
                              highlightsData[4][0] === undefined ||
                              highlightsData[4][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-send-profile' />{" "}
                              Spend Profile Discussed
                            </p>
                            <span>
                              {highlightsData[5][0] === "NULL" ||
                              highlightsData[5][0] === undefined ||
                              highlightsData[5][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <Divider />

                        <div className='legend-flex'>
                          <div>
                            <p>
                              <SquareIcon className='color-data-history' /> Data
                              History Mentioned
                            </p>
                            <span>
                              {highlightsData[6][0] === "NULL" ||
                              highlightsData[6][0] === undefined ||
                              highlightsData[6][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-rate-match' /> Rate
                              Match Suggested
                            </p>
                            <span>
                              {highlightsData[7][0] === "" ||
                              highlightsData[7][0] === undefined ||
                              highlightsData[7][0] === "" ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    ) : (
                      <Skeleton width='100%' height='100%' />
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Modal>
      )}
    </FadeIn>
  );
}

export default AgentEffectiveness;
